import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getProposal, updateProposal, copilotAction, draftEmail, sendEmail,
  uploadAttachment, deleteAttachment, pdfUrl, docxUrl, simulateOpen,
} from "@/lib/api";
import {
  ArrowLeft, Sparkles, Send, Download, Plus, GripVertical, Trash2, Copy,
  ChevronDown, CheckCircle2, Loader2, Wand2, FileText, X, Paperclip, Eye,
  Share2, Link2, CheckCheck, RefreshCw, MoreHorizontal,
} from "lucide-react";
import { useComingSoon } from "@/components/ComingSoon";
import CoverPage, { resolveVars } from "@/components/CoverPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const AI_ACTIONS = [
  { id: "rewrite_professional", label: "Rewrite professionally" },
  { id: "shorten", label: "Shorten" },
  { id: "persuasive", label: "Make persuasive" },
  { id: "add_roi", label: "Add ROI framing" },
  { id: "mention_soc2", label: "Mention SOC2" },
  { id: "executive", label: "Executive-friendly" },
  { id: "technical", label: "Add technical depth" },
  { id: "benefits", label: "List customer benefits" },
];

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const cs = useComingSoon();
  const [proposal, setProposal] = useState(null);
  const [saving, setSaving] = useState("saved"); // saving | saved | error
  const [sendOpen, setSendOpen] = useState(false);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const timer = useRef(null);
  const skipSaveRef = useRef(false);

  useEffect(() => {
    getProposal(id).then(setProposal).catch(() => toast.error("Could not load"));
  }, [id]);

  const persist = useCallback(async (patch) => {
    setSaving("saving");
    try {
      await updateProposal(id, patch);
      setSaving("saved");
    } catch {
      setSaving("error");
    }
  }, [id]);

  // Autosave (debounced) whenever proposal changes
  useEffect(() => {
    if (!proposal) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      persist({
        title: proposal.title,
        company_name: proposal.company_name,
        prospect_name: proposal.prospect_name,
        sections: proposal.sections,
        metadata: proposal.metadata,
        attachments: proposal.attachments,
      });
    }, 700);
    return () => clearTimeout(timer.current);
  }, [proposal, persist]);

  // Near-real-time open tracking — poll while the proposal has been sent
  useEffect(() => {
    if (!proposal || !["sent", "opened", "approved", "converted"].includes(proposal.status)) return;
    const poll = setInterval(async () => {
      try {
        const fresh = await getProposal(id);
        skipSaveRef.current = true;
        setProposal((p) => (p ? { ...p, open_count: fresh.open_count, status: fresh.status, last_opened_at: fresh.last_opened_at, opened_at: fresh.opened_at } : p));
      } catch {
        /* silent — next poll retries */
      }
    }, 20000);
    return () => clearInterval(poll);
  }, [proposal?.status, id]);

  const pushHistory = (next) => {
    undoStack.current.push(JSON.stringify(proposal.sections));
    redoStack.current = [];
    setProposal({ ...proposal, sections: next });
  };

  const undo = () => {
    if (!undoStack.current.length) return;
    redoStack.current.push(JSON.stringify(proposal.sections));
    const prev = JSON.parse(undoStack.current.pop());
    setProposal({ ...proposal, sections: prev });
  };
  const redo = () => {
    if (!redoStack.current.length) return;
    undoStack.current.push(JSON.stringify(proposal.sections));
    const next = JSON.parse(redoStack.current.pop());
    setProposal({ ...proposal, sections: next });
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && (e.key === "y" || (e.shiftKey && e.key === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!proposal) {
    return (
      <div className="min-h-[80vh] grid place-items-center">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  }

  const updateSection = (sid, patch) => {
    const next = proposal.sections.map((s) => (s.id === sid ? { ...s, ...patch } : s));
    pushHistory(next);
  };
  const removeSection = (sid) => pushHistory(proposal.sections.filter((s) => s.id !== sid));
  const duplicateSection = (sid) => {
    const i = proposal.sections.findIndex((s) => s.id === sid);
    if (i < 0) return;
    const copy = { ...proposal.sections[i], id: cryptoRandom(), title: proposal.sections[i].title + " (copy)" };
    const next = [...proposal.sections.slice(0, i + 1), copy, ...proposal.sections.slice(i + 1)];
    pushHistory(next);
  };
  const addSection = () => {
    pushHistory([...proposal.sections, { id: cryptoRandom(), title: "New Section", content: "" }]);
  };
  const moveSection = (from, to) => {
    if (to < 0 || to >= proposal.sections.length) return;
    const next = [...proposal.sections];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    pushHistory(next);
  };

  const runAi = async (sid, action) => {
    const sec = proposal.sections.find((s) => s.id === sid);
    if (!sec?.content?.trim()) {
      toast.error("Section is empty");
      return;
    }
    const t = toast.loading("AI rewriting…");
    try {
      const { text } = await copilotAction(id, {
        action,
        selected_text: sec.content,
        section_title: sec.title,
      });
      updateSection(sid, { content: text });
      toast.success("Updated", { id: t });
    } catch {
      toast.error("Failed", { id: t });
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Editor center */}
      <div className="flex-1 min-w-0">
        <TopBar
          proposal={proposal}
          saving={saving}
          onBack={() => nav("/")}
          onSend={() => setSendOpen(true)}
          onTitle={(v) => setProposal({ ...proposal, title: v })}
          onDownloadPDF={() => window.open(pdfUrl(id), "_blank")}
          onDownloadDOCX={() => window.open(docxUrl(id), "_blank")}
          onSimulateOpen={async () => {
            await simulateOpen(id);
            const fresh = await getProposal(id);
            setProposal(fresh);
            toast.success(`Open tracked (${fresh.open_count || 1})`);
          }}
          onCopyLink={async () => {
            try {
              await navigator.clipboard.writeText(window.location.href);
              toast.success("Link copied");
            } catch {
              toast.error("Could not copy");
            }
          }}
          onShare={() => cs("Share via email/Slack", "Public share links, Slack sharing and email invites are coming soon.")}
          onApprove={() => cs("Approvals", "Approval workflows with signature capture are coming soon.")}
          onRegenerate={() => cs("Regenerate proposal", "One-click regenerate from the original transcript is coming soon.")}
        />
        <div className="max-w-3xl mx-auto px-10 py-10 space-y-8">
          <CoverPage proposal={proposal} setProposal={setProposal} />
          <div className="rounded-2xl bg-white border border-[#E8EAF3] editor-shell">
            <div className="px-12 py-12">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#011B90] mb-3">Proposal body</div>
              <p className="text-xs text-[#9098A8] mb-6">
                Variables like <code className="font-mono bg-[#EDEFF7] px-1.5 py-0.5 rounded text-[#011B90]">{"{{Company}}"}</code>, <code className="font-mono bg-[#EDEFF7] px-1.5 py-0.5 rounded text-[#011B90]">{"{{Client}}"}</code>, <code className="font-mono bg-[#EDEFF7] px-1.5 py-0.5 rounded text-[#011B90]">{"{{Date}}"}</code>, <code className="font-mono bg-[#EDEFF7] px-1.5 py-0.5 rounded text-[#011B90]">{"{{Pricing}}"}</code> auto-populate from the cover page.
              </p>

              <div className="mt-6 space-y-6">
                {proposal.sections.map((s, idx) => (
                  <SectionBlock
                    key={s.id}
                    section={s}
                    resolved={resolveVars(s.content, proposal)}
                    onChange={(patch) => updateSection(s.id, patch)}
                    onDelete={() => removeSection(s.id)}
                    onDuplicate={() => duplicateSection(s.id)}
                    onUp={() => moveSection(idx, idx - 1)}
                    onDown={() => moveSection(idx, idx + 1)}
                    onAi={(a) => runAi(s.id, a)}
                  />
                ))}
              </div>

              <button
                data-testid="add-section-btn"
                onClick={addSection}
                className="mt-8 text-xs text-[#011B90] hover:text-[#011670] flex items-center gap-1.5"
              >
                <Plus size={14} /> Add section
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <RightPanel
        proposal={proposal}
        setProposal={(p) => setProposal(p)}
        onUpload={async (file) => {
          const a = await uploadAttachment(id, file);
          setProposal({ ...proposal, attachments: [...(proposal.attachments || []), a] });
          toast.success("Attached");
        }}
        onRemoveAttachment={async (aid) => {
          await deleteAttachment(id, aid);
          setProposal({ ...proposal, attachments: (proposal.attachments || []).filter((a) => a.id !== aid) });
        }}
        onRunCopilot={async ({ action, text }) => {
          const r = await copilotAction(id, { action: action || "rewrite_professional", selected_text: text, instruction: action ? "" : "Improve the writing" });
          return r.text;
        }}
      />

      <SendDialog open={sendOpen} onOpenChange={setSendOpen} proposalId={id} proposal={proposal} />
    </div>
  );
}

function cryptoRandom() {
  return (crypto.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
}

function TopBar({ proposal, saving, onBack, onSend, onDownloadPDF, onDownloadDOCX, onSimulateOpen, onCopyLink, onShare, onApprove, onRegenerate }) {
  const isSent = ["sent", "opened", "approved", "converted"].includes(proposal.status);
  return (
    <div className="sticky top-0 z-10 backdrop-blur bg-white/80 border-b border-[#E8EAF3]">
      <div className="max-w-5xl mx-auto px-10 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button data-testid="editor-back-btn" onClick={onBack} className="h-8 w-8 grid place-items-center rounded-md hover:bg-[#EDEFF7] text-[#5F6677]">
            <ArrowLeft size={16} />
          </button>
          <div className="text-sm text-[#0B1736] truncate max-w-[220px]">{proposal.title}</div>
          <SaveIndicator state={saving} />
          {isSent && proposal.open_count > 0 && (
            <span data-testid="opens-badge" className="text-[11px] px-2 py-1 rounded-full bg-[#EDEFF7] text-[#011B90] border border-[#011B90]/30 flex items-center gap-1">
              <Eye size={11} /> {proposal.open_count} open{proposal.open_count > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="more-actions-btn" title="More" className="h-9 w-9 grid place-items-center rounded-md border border-[#E8EAF3] bg-white text-[#5F6677] hover:text-[#0B1736] hover:border-[#011B90]/30">
                <MoreHorizontal size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="copy-link-btn" onClick={onCopyLink}><Link2 size={14} className="mr-2" />Copy link</DropdownMenuItem>
              <DropdownMenuItem data-testid="share-btn" onClick={onShare}><Share2 size={14} className="mr-2" />Share</DropdownMenuItem>
              <DropdownMenuItem data-testid="regenerate-btn" onClick={onRegenerate}><RefreshCw size={14} className="mr-2" />Regenerate</DropdownMenuItem>
              {isSent && (
                <DropdownMenuItem data-testid="simulate-open-btn" onClick={onSimulateOpen}><Eye size={14} className="mr-2" />Simulate open (test)</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid="approve-btn" title="Approve" onClick={onApprove} className="h-9 px-2.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1 text-xs">
            <CheckCheck size={13} /> Approve
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="download-btn" variant="outline" className="h-9 rounded-md border-[#E8EAF3]">
                <Download size={14} className="mr-1.5" /> Download <ChevronDown size={14} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="download-pdf" onClick={onDownloadPDF}><FileText size={14} className="mr-2" />PDF (editable)</DropdownMenuItem>
              <DropdownMenuItem data-testid="download-docx" onClick={onDownloadDOCX}><FileText size={14} className="mr-2" />DOCX</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="send-btn" onClick={onSend} className="bg-[#011B90] hover:bg-[#011670] text-white h-9 rounded-md shadow-[0_8px_24px_-8px_rgba(1, 27, 144,0.55)]">
            <Send size={14} className="mr-1.5" /> Send Proposal
          </Button>
        </div>
      </div>
    </div>
  );
}

function SaveIndicator({ state }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-zinc-500">
      {state === "saving" && (<><Loader2 size={12} className="animate-spin" /> Saving…</>)}
      {state === "saved" && (<><CheckCircle2 size={12} className="text-emerald-500" /> Saved</>)}
      {state === "error" && (<span className="text-red-600">Save failed</span>)}
    </div>
  );
}

function SectionBlock({ section, resolved, onChange, onDelete, onDuplicate, onUp, onDown, onAi }) {
  const showResolved = resolved && resolved !== section.content && /\{\{|\}\}/.test(section.content || "");
  return (
    <div data-testid={`section-${section.id}`} className="group relative">
      <div className="absolute -left-10 top-1 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
        <button data-testid={`section-up-${section.id}`} onClick={onUp} className="h-6 w-6 grid place-items-center text-zinc-400 hover:text-zinc-900 rounded-md hover:bg-zinc-100" title="Move up"><GripVertical size={14} /></button>
      </div>
      <div className="flex items-center justify-between">
        <input
          data-testid={`section-title-${section.id}`}
          value={section.title}
          onChange={(e) => onChange({ title: e.target.value })}
          className="font-display text-2xl tracking-tight bg-transparent w-full outline-none border-b border-transparent focus:border-zinc-200 py-1"
        />
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`section-ai-${section.id}`} className="ai-action-chip"><Wand2 size={12} /> AI</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {AI_ACTIONS.map((a) => (
                <DropdownMenuItem key={a.id} onClick={() => onAi(a.id)}>{a.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={onDuplicate} className="h-7 w-7 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100" title="Duplicate"><Copy size={14} /></button>
          <button onClick={onDown} className="h-7 w-7 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100" title="Move down"><ChevronDown size={14} /></button>
          <button onClick={onDelete} className="h-7 w-7 grid place-items-center rounded-md text-zinc-500 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
        </div>
      </div>
      <Textarea
        data-testid={`section-content-${section.id}`}
        value={section.content}
        onChange={(e) => onChange({ content: e.target.value })}
        rows={Math.max(4, Math.min(20, (section.content || "").split("\n").length + 1))}
        className="mt-3 border-0 focus-visible:ring-0 resize-none bg-transparent px-0 text-[15px] leading-relaxed text-zinc-800"
      />
      {showResolved && (
        <div className="mt-1 rounded-md bg-[#EDEFF7]/60 border border-[#011B90]/20 px-3 py-2 text-[13px] text-[#0B1736] leading-relaxed">
          <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#011B90] mb-1">Live preview</div>
          {resolved}
        </div>
      )}
    </div>
  );
}

function RightPanel({ proposal, setProposal, onUpload, onRemoveAttachment, onRunCopilot }) {
  const [tab, setTab] = useState("metadata");
  const fileRef = useRef(null);
  const m = proposal.metadata || {};

  const setMeta = (patch) => setProposal({ ...proposal, metadata: { ...m, ...patch } });

  return (
    <aside data-testid="right-panel" className="hidden lg:flex w-[320px] shrink-0 border-l border-zinc-200 bg-white flex-col">
      <div className="px-4 pt-4 pb-2 flex items-center gap-1 border-b border-zinc-100">
        {[
          { id: "metadata", label: "Details" },
          { id: "copilot", label: "Copilot" },
          { id: "attachments", label: "Files" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`text-xs px-3 h-8 rounded-md transition-colors ${
              tab === t.id ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {tab === "metadata" && (
          <div className="space-y-4">
            <Field label="Customer Name" tid="meta-customer" value={m.customer_name} onChange={(v) => setMeta({ customer_name: v })} />
            <Field label="Company" tid="meta-company" value={m.company} onChange={(v) => { setMeta({ company: v }); setProposal({ ...proposal, company_name: v, metadata: { ...m, company: v } }); }} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date" tid="meta-date" value={m.proposal_date} onChange={(v) => setMeta({ proposal_date: v })} />
              <Field label="Valid Until" tid="meta-valid" value={m.valid_until} onChange={(v) => setMeta({ valid_until: v })} />
            </div>
            <Field label="Owner" tid="meta-owner" value={m.proposal_owner} onChange={(v) => setMeta({ proposal_owner: v })} />
            <Field label="Email" tid="meta-email" value={m.email} onChange={(v) => setMeta({ email: v })} />
            <Field label="Phone" tid="meta-phone" value={m.phone} onChange={(v) => setMeta({ phone: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Currency" tid="meta-currency" value={m.currency} onChange={(v) => setMeta({ currency: v })} />
              <Field label="Pricing" tid="meta-pricing" value={m.pricing} onChange={(v) => setMeta({ pricing: v })} />
            </div>
            <Field label="Discount" tid="meta-discount" value={m.discount} onChange={(v) => setMeta({ discount: v })} />
          </div>
        )}

        {tab === "copilot" && <Copilot onRun={onRunCopilot} />}

        {tab === "attachments" && (
          <div className="space-y-3">
            <button
              data-testid="upload-attachment-btn"
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border border-dashed border-zinc-300 p-4 text-xs text-zinc-600 hover:border-zinc-900 hover:text-zinc-900 transition-colors flex items-center justify-center gap-2"
            >
              <Paperclip size={14} /> Upload attachment
            </button>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
            {(proposal.attachments || []).map((a) => (
              <div key={a.id} data-testid={`attachment-${a.id}`} className="flex items-center gap-3 rounded-lg border border-zinc-200 px-3 py-2.5">
                <FileText size={16} className="text-zinc-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{a.name}</div>
                  <div className="text-[10px] text-zinc-500">{Math.round((a.size || 0) / 1024)} KB</div>
                </div>
                <button onClick={() => onRemoveAttachment(a.id)} className="text-zinc-400 hover:text-red-600"><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function Field({ label, value, onChange, tid }) {
  return (
    <div>
      <div className="label-eyebrow mb-1.5">{label}</div>
      <Input data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 bg-white border-zinc-200" />
    </div>
  );
}

function Copilot({ onRun }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "ai", text: "Paste text or describe what you'd like to improve. I can rewrite, shorten, add ROI, mention SOC2 and more." },
  ]);
  const [busy, setBusy] = useState(false);

  const run = async (action) => {
    if (!input.trim()) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: input }]);
    try {
      const out = await onRun({ action, text: input });
      setMessages((m) => [...m, { role: "ai", text: out }]);
    } catch {
      toast.error("AI failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="space-y-2">
        {messages.map((m, i) => (
          <div key={i} className={`text-sm rounded-lg px-3 py-2 ${m.role === "ai" ? "bg-zinc-50 text-zinc-700" : "bg-zinc-900 text-white"}`}>
            <pre className="whitespace-pre-wrap font-sans">{m.text}</pre>
          </div>
        ))}
      </div>
      <Textarea
        data-testid="copilot-input"
        placeholder="Paste text to transform…"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={5}
        className="border-zinc-200"
      />
      <div className="flex flex-wrap gap-1.5">
        {AI_ACTIONS.map((a) => (
          <button
            key={a.id}
            data-testid={`copilot-${a.id}`}
            disabled={busy}
            onClick={() => run(a.id)}
            className="ai-action-chip disabled:opacity-50"
          >
            <Sparkles size={11} /> {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SendDialog({ open, onOpenChange, proposalId, proposal }) {
  const [form, setForm] = useState({ to: "", cc: "", bcc: "", subject: "", body: "" });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, to: proposal?.metadata?.email || "" }));
    setDrafting(true);
    draftEmail(proposalId)
      .then((d) => setForm((f) => ({ ...f, subject: d.subject || "", body: d.body || "" })))
      .finally(() => setDrafting(false));
    // Intentionally NOT depending on `proposal` (it gets a new reference on every
    // background tracking poll) — only re-draft when the dialog is opened for a given proposal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposalId]);

  const send = async () => {
    setSending(true);
    try {
      const r = await sendEmail(proposalId, form);
      toast.success(r.message || "Sent");
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (err?.message || "Send failed");
      toast.error("Send failed", { description: msg, duration: 9000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Send proposal</DialogTitle>
          <div data-testid="send-live-notice" className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-block">
            Live via Resend · sending from verified domain quowin.io — reaches any prospect
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="To" tid="send-to" value={form.to} onChange={(v) => setForm({ ...form, to: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="CC" tid="send-cc" value={form.cc} onChange={(v) => setForm({ ...form, cc: v })} />
            <Field label="BCC" tid="send-bcc" value={form.bcc} onChange={(v) => setForm({ ...form, bcc: v })} />
          </div>
          <Field label="Subject" tid="send-subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
          <div>
            <div className="label-eyebrow mb-1.5">Body {drafting && <span className="ml-1 text-zinc-400">· AI drafting…</span>}</div>
            <Textarea data-testid="send-body" rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="border-zinc-200" />
          </div>
          {(proposal.attachments || []).length > 0 && (
            <div className="text-xs text-zinc-500">
              {proposal.attachments.length} attachment{proposal.attachments.length > 1 ? "s" : ""} will be included.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="send-submit-btn" onClick={send} disabled={sending} className="bg-[#011B90] hover:bg-[#011670] text-white">
            {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
